import { ChildProps } from '@apollo/client/react/hoc';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React, { useState } from 'react';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { connect } from '@reverbdotcom/commons/src/connect';
import I18n from 'i18n-js';
import Suggestion from '@reverbdotcom/commons/src/components/site_search/suggestion';
import { CoreHomepageRecentSearchesListingsSearch } from '@reverbdotcom/commons/src/gql/graphql';
import TrendingSearchesRecommendations from './trending_searches_recommendations';
import RecentSearchesRecommendations from './recent_searches_recommendations';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

const DEFAULT_SEARCH_REC_COUNT = 3;

interface IExternalProps {
  recentSearches: Suggestion[];
}

export type IProps = ChildProps<IExternalProps, CoreHomepageRecentSearchesListingsSearch.Query>;

export const RecentSearchListingData = gql`
  fragment RecentSearchListingData on Listing {
    _id
    id
    images(input: {
      type: "Product",
      scope: "photos",
      transform: "card_square"
      count: 2
    }) {
      _id
      source
    }
  }
`;

export const homepageSearchRecommendationCount = () => {
  return DEFAULT_SEARCH_REC_COUNT;
};

function dedupeSearches(recentSearches) {
  const lowercaseSearches = recentSearches.map(search => search.query.toLowerCase());
  return recentSearches.filter((search, index) => lowercaseSearches.indexOf(search.query.toLowerCase()) === index);
}

function buildRecentSearchesWithListings(recentSearches, data, user) {
  const decoratedRecentSearches = recentSearches.map((search, idx)=> {
    const queryParams = {
      query: search.value,
      ships_to: user.shippingRegionCode || EVERYWHERE_CODE,
    };

    const totalText = I18n.t(
      'discovery.homepage.searchRecommendations.totalText',
      {
        count: data[`recentSearchListings${idx}`]?.total || 0,
        formattedCount: I18n.toNumber(data[`recentSearchListings${idx}`]?.total, { precision: 0 }),
      },
    );

    return ({
      query: search.value,
      totalText,
      listings: data[`recentSearchListings${idx}`]?.listings || [],
      queryParams: JSON.stringify(queryParams),
    });
  });
  const searchesWithResults = decoratedRecentSearches.filter(s => s.listings.length);
  return dedupeSearches(searchesWithResults);
}

export function HomepageSearchRecommendations(props: IProps) {
  const { recentSearches } = props;
  const user = useUser();
  const [ recentSearchesWithResults, setRecentSearchesWithResults] = useState<any>([]);
  const loading = props.data?.loading;

  React.useEffect(() => {
    if (!!recentSearches.length && !loading) {
      const searchesWithResults = buildRecentSearchesWithListings(recentSearches, props.data, user);
      setRecentSearchesWithResults(searchesWithResults);
    }
  }, [recentSearches.length, loading]);

  return (
    (recentSearchesWithResults.length >= DEFAULT_SEARCH_REC_COUNT || loading) ?
      <RecentSearchesRecommendations
        recentSearches={recentSearchesWithResults}
        loading={loading} />
      : <TrendingSearchesRecommendations />
  );
}
export const RECENT_SEARCHES_LISTINGS_SEARCH = gql`query Core_Homepage_RecentSearchesListingsSearch(
  $limit: Int
  $query0: String
  $query1: String
  $query2: String
  $skipQuery2: Boolean!
  $query3: String
  $skipQuery3: Boolean!
) {
  recentSearchListings0: listingsSearch(input: {
    limit: $limit,
    query: $query0,
  }) {
    total
    listings {
      _id
      slug
      ...RecentSearchListingData
    }
  }
  recentSearchListings1: listingsSearch(input: {
    limit: $limit,
    query: $query1,
  }) {
    total
    listings {
      _id
      slug
      ...RecentSearchListingData
    }
  }
  recentSearchListings2: listingsSearch(input: {
    limit: $limit,
    query: $query2,
  }) @skip(if: $skipQuery2) {
    total
    listings {
      _id
      slug
      ...RecentSearchListingData
    }
  }
  recentSearchListings3: listingsSearch(input: {
    limit: $limit,
    query: $query3,
  }) @skip(if: $skipQuery3) {
    total
    listings {
      _id
      slug
      ...RecentSearchListingData
    }
  }
}
  ${RecentSearchListingData}
`;

const withListingsSearchQuery = withGraphql<
  IProps,
  CoreHomepageRecentSearchesListingsSearch.Query,
  CoreHomepageRecentSearchesListingsSearch.Variables
>(
  RECENT_SEARCHES_LISTINGS_SEARCH,
  {
    options: (props: IProps) => ({
      skip: !(props.recentSearches?.length >= DEFAULT_SEARCH_REC_COUNT),
      ssr: false,
      variables: {
        limit: 2,
        query0: props.recentSearches[0]?.value,
        query1: props.recentSearches[1]?.value,
        // we are fetching more than we need here in case any of these listings searches don't have results
        query2: props.recentSearches[2]?.value,
        skipQuery2: !props.recentSearches[2],
        query3: props.recentSearches[3]?.value,
        skipQuery3: !props.recentSearches[3],
      },
    }),
  },
);

export default connect<IExternalProps>([
  withListingsSearchQuery,
])(HomepageSearchRecommendations);
